import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Star, Copy, QrCode, Loader2, MessageSquare, ExternalLink, Mail,
  CheckCircle2, Clock, XCircle,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeader = () => {
  const t = localStorage.getItem("admin_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

// Use the current host so the share link works on whatever domain the portal
// is running on (staging preview today, magnuskor.com after deployment).
const PUBLIC_BASE = (typeof window !== "undefined" && window.location?.origin)
  ? window.location.origin
  : "https://magnuskor.com";

const STATUS_META = {
  pending:  { label: "Pending Review", Icon: Clock,         tone: "text-amber-600 bg-amber-50 border-amber-200" },
  approved: { label: "Approved",       Icon: CheckCircle2,  tone: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  rejected: { label: "Rejected",       Icon: XCircle,       tone: "text-red-600 bg-red-50 border-red-200" },
};

export function MyTestimonialsTab({ profile }) {
  const [tab, setTab] = useState("mine"); // "mine" | "share"
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const memberId = profile?.member_id || "";
  const shareUrl = memberId ? `${PUBLIC_BASE}/share/${memberId}` : "";
  // Use a CDN QR for simplicity (no extra deps)
  const qrSrc = shareUrl
    ? `https://api.qrserver.com/v1/create-qr-code/?size=320x320&color=1A1A1A&data=${encodeURIComponent(shareUrl)}`
    : "";

  useEffect(() => {
    if (tab !== "mine") return;
    setLoading(true);
    axios.get(`${API}/testimonials/my-credits`, { headers: authHeader() })
      .then(({ data }) => setItems(Array.isArray(data) ? data : []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [tab]);

  const copy = (txt) => {
    if (!txt) return;
    navigator.clipboard.writeText(txt);
    toast.success("Copied to clipboard");
  };

  const emailToClient = () => {
    if (!shareUrl) return;
    const subject = encodeURIComponent("Would love your feedback on our work together");
    const body = encodeURIComponent(
      `Hi,\n\nThank you for working with me on the recent placement. ` +
      `If you have a moment, I'd really appreciate a quick testimonial — your feedback ` +
      `helps us continue doing great work.\n\nJust click this link to share your experience:\n${shareUrl}\n\n` +
      `It takes about 2 minutes.\n\nThanks again,\n${profile?.name || ""}\nMagnus Kor Talent Partners`
    );
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  return (
    <div className="p-6" data-testid="my-testimonials-tab">
      <div className="flex items-center gap-2 mb-1">
        <MessageSquare size={18} className="text-[#B8860B]" strokeWidth={1.5} />
        <h3 className="font-medium text-lg">Testimonials</h3>
      </div>
      <p className="text-sm text-gray-500 mb-6">
        Reviews credited to you, and your personal sharing link for clients and candidates.
      </p>

      {/* Sub-tabs */}
      <div className="flex gap-1 mb-6 border-b border-gray-100">
        <button onClick={() => setTab("mine")}
          className={`px-4 py-2 text-sm border-b-2 transition-colors ${tab === "mine" ? "border-[#B8860B] text-[#1A1A1A]" : "border-transparent text-gray-500 hover:text-gray-700"}`}
          data-testid="subtab-mine">
          My Testimonials
        </button>
        <button onClick={() => setTab("share")}
          className={`px-4 py-2 text-sm border-b-2 transition-colors ${tab === "share" ? "border-[#B8860B] text-[#1A1A1A]" : "border-transparent text-gray-500 hover:text-gray-700"}`}
          data-testid="subtab-share">
          Share Link
        </button>
      </div>

      {tab === "mine" ? (
        loading ? (
          <div className="text-center py-12 text-gray-400 text-sm">
            <Loader2 className="animate-spin inline mr-2" size={14} /> Loading…
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-12 border border-dashed border-gray-200 rounded-lg" data-testid="empty-state">
            <MessageSquare className="text-gray-300 mx-auto mb-3" size={28} strokeWidth={1.2} />
            <p className="text-sm text-gray-500">No testimonials credited to you yet.</p>
            <p className="text-xs text-gray-400 mt-1">Share your link with clients after a successful placement to start collecting them.</p>
            <button onClick={() => setTab("share")} className="mt-4 text-xs text-[#B8860B] hover:underline">
              Go to share link →
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((t) => {
              const meta = STATUS_META[t.status] || STATUS_META.pending;
              return (
                <div key={t.id} className="border border-gray-100 rounded-lg p-5 bg-white" data-testid={`testimonial-${t.id}`}>
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div>
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="font-medium text-sm text-gray-800">{t.contact_name}</span>
                        <span className="text-xs text-gray-400">·</span>
                        <span className="text-xs text-gray-500">{t.role} at {t.company_name}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        {[1,2,3,4,5].map((s) => (
                          <Star key={s} size={12} className={s <= (t.rating || 0) ? "text-[#B8860B]" : "text-gray-200"} fill={s <= (t.rating || 0) ? "#B8860B" : "transparent"} />
                        ))}
                      </div>
                    </div>
                    <span className={`flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 border rounded-full ${meta.tone}`}>
                      <meta.Icon size={10} /> {meta.label}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700 leading-relaxed mt-2 italic">"{t.outcome || t.solution || t.problem}"</p>
                  <div className="text-[11px] text-gray-400 mt-3">
                    Submitted {new Date(t.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
                  </div>
                </div>
              );
            })}
          </div>
        )
      ) : (
        /* Share view */
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="border border-gray-100 rounded-lg p-5 bg-white">
            <h4 className="text-xs uppercase tracking-wider text-[#B8860B] font-medium mb-3">Your personal testimonial link</h4>
            {memberId ? (
              <>
                <div className="flex items-center gap-2 border border-gray-200 px-3 py-2 rounded bg-gray-50">
                  <input readOnly value={shareUrl}
                    className="flex-1 bg-transparent text-sm text-gray-700 outline-none" data-testid="share-url-input" />
                  <button onClick={() => copy(shareUrl)} className="text-xs text-[#B8860B] hover:underline flex items-center gap-1" data-testid="copy-share-url">
                    <Copy size={12} /> Copy
                  </button>
                </div>

                <div className="mt-4 flex gap-2">
                  <button onClick={emailToClient}
                    className="flex-1 flex items-center justify-center gap-2 bg-[#B8860B] hover:bg-[#9A7209] text-white text-sm py-2.5 rounded transition-colors"
                    data-testid="email-share-btn">
                    <Mail size={14} /> Email to a client
                  </button>
                  <a href={shareUrl} target="_blank" rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 border border-gray-200 hover:border-[#B8860B] text-gray-700 text-sm px-4 py-2.5 rounded transition-colors"
                    data-testid="preview-share-link">
                    <ExternalLink size={14} /> Preview
                  </a>
                </div>

                <div className="mt-6 text-xs text-gray-500 leading-relaxed">
                  <p className="font-medium text-gray-700 mb-1">How it works</p>
                  <ol className="list-decimal list-inside space-y-1">
                    <li>Send this link to a client or candidate after a successful interaction.</li>
                    <li>They fill in a 2-minute form (the page tells them they're crediting <span className="font-medium">{profile?.name}</span>).</li>
                    <li>Once admin approves, the testimonial appears here and on magnuskor.com.</li>
                  </ol>
                </div>
              </>
            ) : (
              <p className="text-sm text-gray-500">Your account doesn't have a Member ID yet. Ask your admin to assign one so you can start collecting testimonials.</p>
            )}
          </div>

          <div className="border border-gray-100 rounded-lg p-5 bg-white flex flex-col items-center justify-center text-center">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-[#B8860B] font-medium mb-3 self-start">
              <QrCode size={12} /> QR code
            </div>
            {qrSrc ? (
              <>
                <img src={qrSrc} alt="Testimonial QR code" className="w-48 h-48 border border-gray-100 rounded" data-testid="share-qr" />
                <p className="text-xs text-gray-500 mt-3 max-w-xs">Drop this into your email signature, business card, or pin it on LinkedIn so clients can scan and review you instantly.</p>
                <a href={qrSrc} download={`magnus-kor-${memberId}-testimonial-qr.png`} className="mt-3 text-xs text-[#B8860B] hover:underline" data-testid="qr-download">
                  Download QR
                </a>
              </>
            ) : (
              <p className="text-sm text-gray-500">QR will appear once you have a Member ID.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default MyTestimonialsTab;
